import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { showToast } from '../../../services';
import { userActions } from '../../../actions';
import { profileActions } from '../../../actions';
import { useLoggedUser } from '../../../hooks/useLoggedUser';

import userImage from '../../../assets/img/user.svg';
import menuIcon from '../../../assets/img/menu-icon.svg';
import questionIcon from '../../../assets/img/question.svg';
import leftArrow from '../../../assets/img/left-arrow.svg';
import rightArrow from '../../../assets/img/right-arrow.svg';
import cameraIcon from '../../../assets/img/camera90.svg';
import Close from '../../../assets/img/close.svg';

const initialState = {
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    age: '',
    gender: '',
    avatarid: null,
    avatarmedia: '',
    invalidusername: false,
    selfieimage: false,
    loggedinstatus: 'Y',
    submitted: false,
    firstnameError: '',
    lastnameError: '',
    ageError: ''
};

const RegisterPage = () => {
    const [state, setState] = useState(initialState);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [selfieMessage, setSelfieMessage] = useState('');
    const profileModalRef = useRef(null);
    const profileModalTriggerRef = useRef(null);
    const selfieAlertModalRef = useRef(null);
    const dispatch = useDispatch();
    const history = useHistory();

    const loggedUser = useLoggedUser();
    const { loading, myprofile, error } = useSelector((state) => state.myprofile);
    const { avatars } = useSelector((state) => state.users);


    // Redirect to /home if student already registered
    useEffect(() => {
        if (loggedUser?.data?.LoggedInStatus === "Y") {
            history.replace('/');
        }
    }, [history, loggedUser?.data?.LoggedInStatus]);

    useEffect(() => {
        if (!myprofile || myprofile.length === 0) {
            setState(initialState); // Reset component state
        }
    }, [myprofile]);

    useEffect(() => {
        if (loggedUser) {
            dispatch(profileActions.fetchMyprofile(loggedUser.data.StudentProfileID));
        }
    }, [dispatch, loggedUser?.data?.StudentProfileID]);

    useEffect(() => {
        if (myprofile && myprofile.length > 0) {
            // Populate state with the fetched profile data
            setState({
                ...initialState,
                username: myprofile[0]?.UserName || '',
                firstname: myprofile[0]?.FirstName || '',
                lastname: myprofile[0]?.LastName || '',
                email: myprofile[0]?.EmailAddress || '',
                age: myprofile[0]?.Age || '',
                gender: myprofile[0]?.Gender || '',
                avatarmedia: myprofile[0]?.AvatarMedia || '',
                submitted: false
            });
        }
    }, [myprofile]);

    useEffect(() => {
        if (loggedUser) {
            dispatch(userActions.fetchprofileavatars(loggedUser.data.StudentProfileID));
        }
    }, [dispatch]);

    useEffect(() => {
        const bindModalFocusManagement = (modalRef, focusTargetRef) => {
            const modalElement = modalRef.current;

            if (!modalElement) {
                return () => {};
            }

            const handleHide = () => {
                const activeElement = document.activeElement;

                if (activeElement instanceof HTMLElement && modalElement.contains(activeElement)) {
                    activeElement.blur();
                }
            };

            const handleHidden = () => {
                const focusTarget = focusTargetRef.current;

                if (focusTarget instanceof HTMLElement) {
                    window.requestAnimationFrame(() => {
                        if (document.querySelector('.modal.show')) {
                            return;
                        }

                        focusTarget.focus();
                    });
                }
            };

            modalElement.addEventListener('hide.bs.modal', handleHide);
            modalElement.addEventListener('hidden.bs.modal', handleHidden);

            return () => {
                modalElement.removeEventListener('hide.bs.modal', handleHide);
                modalElement.removeEventListener('hidden.bs.modal', handleHidden);
            };
        };

        const cleanupProfileModal = bindModalFocusManagement(profileModalRef, profileModalTriggerRef);
        const cleanupSelfieAlertModal = bindModalFocusManagement(selfieAlertModalRef, profileModalTriggerRef);

        return () => {
            cleanupProfileModal();
            cleanupSelfieAlertModal();
        };
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setState((prevState) => ({
            ...prevState,
            [name]: value,
            ...(name === 'username' && { invalidusername: false }),
            ...(name === 'firstname' && { firstnameError: '' }),
            ...(name === 'lastname' && { lastnameError: '' }),
            ...(name === 'age' && { ageError: '' })
        }));
    };

    const goBack = (event) => {
        event.preventDefault();
        history.replace('/login');
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setState((prevState) => ({ ...prevState, submitted: true }));

        const { username, firstname, lastname, age, email } = state;

        // Validation: username is mandatory
        if (!username || !username.trim()) {
            setState((prevState) => ({ ...prevState, submitted: true }));
            return;
        }

        // Allow letters, numbers, safe special characters, and emojis (exclude < > " \ ` ; & { } / and control chars)
        const usernameRegex = /^[a-zA-Z0-9._\s\-'(),!@#$%*+=:?\[\]^|~\p{Emoji}\p{Emoji_Component}\p{Emoji_Presentation}\p{Emoji_Modifier}\p{Emoji_Modifier_Base}]+$/u;
        if (!usernameRegex.test(username.trim())) {
            setState((prevState) => ({
                ...prevState,
                invalidusername: true,
                submitted: true
            }));
            return;
        }
        if (username.trim().length > 15) {
            showToast('Username should not be more than 15 characters', 'error');
            return;
        }

        // Validation: first name (non-mandatory) - letters, spaces, hyphens, apostrophes only; max 50 chars
        const nameRegex = /^[a-zA-Z\s\-']*$/;
        const firstnameTrimmed = (firstname || '').trim();
        const lastnameTrimmed = (lastname || '').trim();
        let firstnameError = '';
        if (firstnameTrimmed) {
            if (!nameRegex.test(firstname)) {
                firstnameError = 'First name can only contain letters, spaces, hyphens and apostrophes';
            } else if (firstnameTrimmed.length > 50) {
                firstnameError = 'First name must be 50 characters or less';
            }
        }

        let lastnameError = '';
        if (lastnameTrimmed) {
            if (!nameRegex.test(lastname)) {
                lastnameError = 'Last name can only contain letters, spaces, hyphens and apostrophes';
            } else if (lastnameTrimmed.length > 50) {
                lastnameError = 'Last name must be 50 characters or less';
            }
        }

        // Validation: age (non-mandatory) - if provided, must be number in range 1-100
        let ageError = '';
        if (age !== '' && age != null) {
            const ageNum = Number(age);
            if (Number.isNaN(ageNum) || !Number.isInteger(ageNum) || ageNum < 1 || ageNum > 100) {
                ageError = 'Age must be a whole number between 1 and 100';
            }
        }

        if (firstnameError || lastnameError || ageError) {
            setState((prevState) => ({
                ...prevState,
                firstnameError,
                lastnameError,
                ageError,
                submitted: true
            }));
            return;
        }

        const profileData = {
                UserName: state.username,
                FirstName: state.firstname,
                LastName: state.lastname,
                EmailAddress: state.email,
                Age: state.age,
                Gender: state.gender,
                AvatarId: state.avatarid,
                SelfieImage: state.selfieimage,
                StudentProfileID: loggedUser.data.StudentProfileID,
                ClassID: loggedUser.data.ClassID,
                LoggedInStatus: state.loggedinstatus
            }

            dispatch(userActions.register(profileData))
                .then((response) => {
                    // Only navigate on clear success; treat any error payload as failure
                    if (response instanceof Error) {
                        showToast(response.message || response.toString(), 'error');
                        return;
                    }
                    if (response?.responseMessage === "Username already exists. Please choose a different one.") {
                        showToast(response.responseMessage, 'warn');
                        return;
                    }
                    if (response?.errorMessage) {
                        showToast(response.errorMessage, 'error');
                        return;
                    }
                    history.push('/favouriteactivities');
                })
                .catch(error => {
                    showToast(error?.message || error.toString(), 'error');
                });
    };

    const handleAvatarClick = (avatarId) => {
        setState((prevState) => ({
            ...prevState,
            avatarid: avatarId
        }));
        setSelectedAvatar(avatarId);
    };

    const handleCameraClick = () => {
        setState((prevState) => ({
            ...prevState,
            selfieimage: true
        }));

        const popupType = "Selfie";
        dispatch(userActions.fetchTermsandConditions(popupType))
            .then((response) => {
                if (response && response.length > 0) {
                    const { MessageText } = response[0];
                    setSelfieMessage(MessageText);
                }
            })
            .catch((error) => {
                showToast(error.toString(), 'error');
            });

        // alert("SELFIE MODE IS UNDER DEVELOPMENT. PLEASE SELECT ANOTHER AVATAR");
    };

    const { username, firstname, lastname, email, age, gender, submitted, avatarid, avatarmedia, selfieimage, firstnameError, lastnameError, ageError } = state;

    return (
        <React.Fragment>
            <div className="continer-fluid signup-page">
                <nav className="navbar navbar-expand-sm">
                    <div className="container-fluid">
                        <a className="navbar-brand">
                            <div className="w-20 d-flex align-items-center justify-content-center">
                                <img className="w-100 d-none" src={menuIcon} alt="Menu Icon" />
                            </div>
                        </a>
                        <div className="collapse navbar-collapse" id="mynavbar">
                            <ul className="navbar-nav me-auto"></ul>
                            <form className="d-flex justify-content-end">
                                <img className="w-100 d-none" src={questionIcon} alt="Question Icon" />
                            </form>
                        </div>
                    </div>
                </nav>

                <div className="content" id="content">
                    <div className="d-flex justify-content-center align-items-left flex-column mt-4">
                        <h2 className="b-font sign-up">Sign Up To Play</h2>
                    </div>

                    <form className="signup-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
                        <div className="d-flex justify-content-between w-100 align-items-end signup-form-div">
                            <div className="left-form">
                                <div className="avatar-upload d-flex flex-column">
                                    <button
                                        type="button"
                                        className="profile-pic-btn"
                                        data-bs-toggle="modal"
                                        data-bs-target="#profile-pic-icon"
                                        ref={profileModalTriggerRef}
                                    >
                                        <img
                                            src={
                                                selectedAvatar && avatars && avatars.length > 0
                                                    ? avatars.find(avatar => avatar.MediaID === selectedAvatar)?.Media_URL
                                                    : avatarmedia || userImage
                                            }
                                            className="avatar-show-img"
                                            alt="Profile"
                                        />
                                    </button>
                                    <label className="b-font pp-text text-center">Add Your Profile Picture</label>
                                </div>
                            </div>

                            <div className='right-form'>
                                <div className={'d-flex flex-column' + (submitted && !username ? ' has-error' : '')}>
                                    <label className="from-label b-font" htmlFor="username">Username*</label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={username}
                                        onChange={handleChange}
                                        tabIndex={1}
                                    />
                                    {submitted && !username && (
                                        <div className="help-block">Username is required</div>
                                    )}
                                    {submitted && state.invalidusername && (
                                        <div className="help-block">Only letters, numbers, periods, emojis and safe special characters (e.g. - _ &#39; , ( ) ! @ # $ % * + = : ? [ ] ^ | ~) are allowed</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between w-100 align-items-center signup-form-div">
                            <div className="left-form-btm">
                                <div className={'d-flex flex-column mt-3' + (firstnameError ? ' has-error' : '')}>
                                    <label className="from-label b-font" htmlFor="firstname">First Name</label>
                                    <input
                                        type="text"
                                        id="firstname"
                                        name="firstname"
                                        value={firstname}
                                        onChange={handleChange}
                                        tabIndex={2}
                                    />
                                    {firstnameError && (
                                        <div className="help-block">{firstnameError}</div>
                                    )}
                                </div>
                            </div>

                            <div className='right-form-btm'>
                                <div className={'d-flex flex-column mt-3' + (lastnameError ? ' has-error' : '')}>
                                    <label className="from-label b-font" htmlFor="lastname">Last Name</label>
                                    <input
                                        type="text"
                                        id="lastname"
                                        name="lastname"
                                        value={lastname}
                                        onChange={handleChange}
                                        tabIndex={3}
                                    />
                                    {lastnameError && (
                                        <div className="help-block">{lastnameError}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between w-100 align-items-center signup-form-div">
                            <div className="left-form-btm">
                                <div className={'d-flex flex-column mt-3' + (submitted && !email ? ' has-error' : '')}>
                                    <label className="from-label b-font" htmlFor="email">Password</label>
                                    <input
                                        type="text"
                                        id="email"
                                        name="email"
                                        value={email}
                                        onChange={handleChange}
                                        tabIndex={4}
                                    />
                                </div>
                            </div>

                            <div className='right-form-btm'>
                                <div className={'d-flex flex-column mt-3' + (ageError ? ' has-error' : '')}>
                                    <label className="from-label b-font" htmlFor="age">Age</label>
                                    <input
                                        type="number"
                                        id="age"
                                        name="age"
                                        value={age}
                                        onChange={handleChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'e' || e.key === '-' || e.key === '+') {
                                                e.preventDefault();
                                            }
                                        }}
                                        min="1"
                                        max="100"
                                        tabIndex={5}
                                    />
                                    {ageError && (
                                        <div className="help-block">{ageError}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="cntr gender-div">
                            <label htmlFor="opt1" className="radio">
                                <input
                                    type="radio"
                                    name="gender"
                                    id="opt1"
                                    className="hidden"
                                    value="Boy"
                                    checked={gender === 'Boy'}
                                    onChange={handleChange}
                                />
                                <span className="label"></span>Boy
                            </label>
                            <label htmlFor="opt2" className="radio">
                                <input
                                    type="radio"
                                    name="gender"
                                    id="opt2"
                                    className="hidden"
                                    value="Girl"
                                    checked={gender === 'Girl'}
                                    onChange={handleChange}
                                />
                                <span className="label"></span>Girl
                            </label>
                            <label htmlFor="opt3" className="radio">
                                <input
                                    type="radio"
                                    name="gender"
                                    id="opt3"
                                    className="hidden"
                                    value="Prefer not to share"
                                    checked={gender === 'Prefer not to share'}
                                    onChange={handleChange}
                                />
                                <span className="label"></span>Prefer not to share
                            </label>
                        </div>
                    </form>

                    <div className="btm-butons">
                        <div className="d-flex w-100 justify-content-between">
                            <button type="button" className="arrow-btns" onClick={goBack}>
                                <img src={leftArrow} className="w-100" alt="Left Arrow" />
                            </button>
                            <button type="button" className="arrow-btns" onClick={handleSubmit}>
                                <img src={rightArrow} className="w-100" alt="Right Arrow" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal starts Hear----------------------------- */}
            <div className="modal" id="profile-pic-icon" tabIndex="-1" role="dialog" aria-labelledby="myBoxLabel" aria-hidden="true" ref={profileModalRef}>
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="up-avatar-title">Select Your Avatar</h4>
                            <button type="button" className="close-btn" data-bs-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true"><img src={Close} alt="" /></span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row avatar-p-row">
                                {avatars && avatars.length > 0 ? (
                                    avatars.map((avatar, index) =>
                                        avatar.AvatarType === "free" && (
                                            <div className="col-md-3 col-lg-3 col-sm-3 mb-4 ml-3" key={avatar.ProfileAvatarID ?? avatar.MediaID ?? `avatar-${index}`}>
                                                <div className="avatar-div d-flex justify-content-center">
                                                    {avatar.Media_URL ? (
                                                        <div className='avatar-img-div'>
                                                            <img
                                                                className={`avatar-img ${selectedAvatar === avatar.MediaID ? 'selected-avatar' : ''}`}
                                                                src={avatar.Media_URL}
                                                                alt="Avatar"
                                                                onClick={() => handleAvatarClick(avatar.MediaID)}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div>Image not available</div>
                                                    )}
                                                </div>
                                                {/* <label className="avatar-text text-center fw-7">XX Coins</label> */}
                                            </div>
                                        )
                                    )
                                ) : (
                                    <div>No avatars available</div>
                                )}
                                <div className="col-md-3 col-lg-3 col-sm-3 mb-5">
                                    <div className="ml-2 camera-div d-flex justify-content-center">
                                        <button
                                            type="button"
                                            className="camera-trigger-btn"
                                            data-bs-toggle="modal"
                                            data-bs-target="#selfiealert"
                                            onClick={handleCameraClick}
                                            aria-label="Open selfie information"
                                        >
                                            <img
                                                className="camera-icn"
                                                src={cameraIcon}
                                                alt=""
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer u-r-modal-f">
                            <button className="accept-btn mt-2 mb-5" data-bs-dismiss="modal" style={{ color: 'white' }}>Select</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal" id="selfiealert" tabIndex="-1" role="dialog" aria-labelledby="myBoxLabel" aria-hidden="true" ref={selfieAlertModalRef}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content selfie-modal">
                        <div className="modal-header">
                            <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                                <span className='close-text' aria-hidden="true"><img className="w-25" src={Close} alt="" /></span>
                            </button>
                        </div>
                        <div className="modal-body selfie-text">
                            <div className="">
                                <h3 className="b-font" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selfieMessage || '') }}></h3>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </React.Fragment>
    );
};

export default RegisterPage;
